/**
 * /programs/:id/preview/days/:dayId — courses scheduled on a single day
 * rendered with the same locked/open/finished tile vocabulary as the days
 * grid. The lock copy uses "Section" to match the day-detail Figma.
 */

import React from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useProgram } from '../../services/programs';
import { useImageUrl } from '../../services/uploads';
import PreviewTile from '../../components/programs/preview/PreviewTile';
import PreviewTileGrid from '../../components/programs/preview/PreviewTileGrid';
import PreviewPageShell from '../../components/programs/preview/PreviewPageShell';

export default function ProgramPreviewDayDetailPage() {
  const { id, dayId } = useParams<{ id: string; dayId: string }>();
  const { data: program, isLoading, isError } = useProgram(id);

  if (isLoading) {
    return (
      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="h-12 w-2/3 rounded-lg bg-text-tertiary/10 animate-pulse" />
        <div className="h-72 rounded-[28px] bg-text-tertiary/10 animate-pulse" />
      </div>
    );
  }
  if (isError || !program || !id) {
    return (
      <div className="max-w-[640px] mx-auto px-4 py-12 text-center">
        <p className="text-sm text-error-500 mb-4">Could not load the program preview.</p>
        <Link to="/programs" className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to Programs
        </Link>
      </div>
    );
  }

  const day = program.days.find((d) => d.id === dayId);
  if (!day) {
    // Day was renamed/deleted since the trainer opened the link — bounce up.
    return <Navigate to={`/programs/${id}/preview/days`} replace />;
  }

  const courses = (day.courses ?? []).slice().sort((a, b) => a.order_index - b.order_index);
  // Preview chain context: ordered day ids + this day's ordered course ids,
  // handed to each course preview so it can offer Next course / Next day.
  const orderedDayIds = (program.days ?? [])
    .slice()
    .sort((a, b) => a.day_number - b.day_number)
    .map((d) => d.id);
  const chain = {
    programId: id!,
    dayIds: orderedDayIds,
    dayIndex: orderedDayIds.indexOf(day.id),
    courseIds: courses.map((dc) => dc.course_id),
  };
  const nextDayId = orderedDayIds[chain.dayIndex + 1];

  return (
    <PreviewPageShell
      programId={id}
      upHref={`/programs/${id}/preview/days`}
      upLabel="Back to Days"
      programIsActive={program.status === 'active'}
    >
      <header className="px-1 sm:px-2">
        <p className="text-sm font-bold text-primary-500/80 uppercase tracking-[0.18em] mb-1">
          Day {day.day_number} of {program.duration_days}
        </p>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-primary-500 leading-tight">
          {day.title || `Day ${day.day_number}`}
        </h2>
      </header>

      {courses.length === 0 ? (
        <p className="text-sm text-text-secondary text-center py-12">
          This day doesn&apos;t have any courses scheduled yet.
        </p>
      ) : (
        <PreviewTileGrid maxColumns={4}>
          {courses.map((dc, i) => (
            <CourseTileCell key={dc.id} index={i} dayCourse={dc} chain={chain} />
          ))}
        </PreviewTileGrid>
      )}

      {/* Smooth preview flow — continue without bouncing off the days list. */}
      <div className="flex justify-end pt-6">
        {nextDayId ? (
          <Link
            to={`/programs/${id}/preview/days/${nextDayId}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            Next day →
          </Link>
        ) : (
          <Link
            to={`/programs/${id}/preview/days`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            Back to all days
          </Link>
        )}
      </div>
    </PreviewPageShell>
  );
}

export interface ProgramPreviewChain {
  programId: string;
  dayIds: string[];
  dayIndex: number;
  courseIds: string[];
}

interface CourseTileCellProps {
  index: number;
  chain: ProgramPreviewChain;
  dayCourse: {
    id: string;
    course_id: string;
    order_index: number;
    course: {
      id: string;
      title: string;
      image_url: string | null;
      status: string;
      estimated_duration_minutes: number | null;
    } | null;
  };
}

function CourseTileCell({ index, dayCourse, chain }: CourseTileCellProps) {
  const navigate = useNavigate();
  const [coverUrl] = useImageUrl(dayCourse.course?.image_url ?? null);
  if (!dayCourse.course) {
    return (
      <div className="aspect-[2/3] rounded-2xl border border-text-tertiary/15 bg-text-tertiary/5 flex items-center justify-center text-text-tertiary text-xs italic px-3 text-center">
        Course unavailable
      </div>
    );
  }
  // Trainer review: every course opens its full view-as-learner preview
  // (the preview page exits with history-back, landing right back here).
  return (
    <PreviewTile
      imageUrl={coverUrl ?? undefined}
      label={dayCourse.course.title}
      state="open"
      entranceDelay={index * 0.05}
      onClick={() =>
        navigate(`/courses/${dayCourse.course_id}/preview`, {
          state: { programChain: { ...chain, courseIndex: index } },
        })
      }
      aspectClassName="aspect-[4/5]"
    />
  );
}
